<!--
  -  Copyright (c) Qwoach LLC 2023. All rights reserved.
  -  Unauthorized copying of this file, via any medium is strictly prohibited
  -  Proprietary and confidential
  -  Created by Artem Bogush <artem@qwoach.com> on 1/1/2023
  -->

<template>
	<div class="qw-uploader-root relative" :style="computedCssVars">
		<div
			v-show="!isStatusUploading && !isStatusUploaded"
			class="el-upload el-upload--picture-card text-center"
			:class="{'circle-border-radius': isCircleComputed}"
			@click="pickFile">
			<i class="fontello icon-plus font-size-8 fontello-mr-0"></i>
			<LocalFilePicker
				@fileSelected="onFileSelected"
				class="absolute"
				ref="localInput"
				:sizeLimit="sizeLimit"
				:filter="imageFilter"
				:filterNames="['image']"
				:loadFileOnSelect="loadFileOnSelect"

			/>
		</div>
		<div v-if="isStatusUploading || isStatusUploaded"
			 class="el-upload-list el-upload-list--picture-card">
			<div class="el-upload-list__item"
				 :class="{'circle-border-radius': isCircleComputed}">

				<div v-if="isStatusUploading">
					<el-progress
						type="circle"
						:percentage="uploadProgress"/>
				</div>
				<div v-else-if="isStatusUploaded" class="preview-container relative">
					<qw-img v-show="localFile.url || qwoachFileData.url"
							ref="imageView"
							class="el-upload-list__item-thumbnail"
							:src="localFile.url || qwoachFileData.url" alt=""/>
					<span class="el-upload-list__item-actions">
						<span
							class="el-upload-list__item-preview"
							@click="handlePreview(qwoachFile)">
						  <i class="fontello icon-zoom-in"></i>
						</span>
						<span
							class="el-upload-list__item-preview"
							@click="handleUpdate(qwoachFile)">
							<i class="fontello icon-arrows-cw"></i>
						</span>
						<span
							class="el-upload-list__item-preview"
							@click="handleRemove(qwoachFile)">
							<i class="fontello icon-trash"></i>
						</span>
					</span>
					<LoadingIndicator/>
				</div>
			</div>

		</div>

	</div>


</template>

<script>
import BaseComponent from "@/core/components/BaseComponent.vue";
import LocalFilePicker from "@/core/components/File/LocalFilePicker.vue";
import {FileUploadStatus} from "@/core/components/File/FileUploadProgress/FileUploadStatus";
import QwoachFile from "@shared/Drive/QwoachFile";
import {FileTypesEnum} from "@/core/components/File/FileTypesEnum";
import LoadingIndicator from "@/core/components/LoadingIndicator.vue";
import {deleteConfirmDialog, imageDialog} from "@/core/components/Dialogs/dialog";
import {classToClass} from "@qwoach/class-transformer-wrapper";

export default {
	name: "QwImageUploader",
	components: {LoadingIndicator, LocalFilePicker},
	extends: BaseComponent,

	props: {
		sizePx: {Number, default: 148},
		qwoachFile: QwoachFile,
		filter: {Array, default: () => []},
		filterNames: {Array, default: () => []},
		sizeLimit: Number,
		loadFileOnSelect: {type: Boolean, default: true},
		parentFolderId: String,
		shape: String,
	},

	data() {
		return {
			fileUploadStatus: null,
			uploadProgress: 0,
			qwoachFileData: null,
			localFile: {},
			fileManager: null,
			isUpdatingFile: false,
			prevFile: null
		}
	},

	watch: {
		qwoachFile() {
			this.syncQwoachFileData()
		}
	},

	created() {
		this.setup()
	},

	methods: {

		setup() {
			this.fileManager = this.$fileUploader.fileManager
			this.syncQwoachFileData()

			// setTimeout(() => {
			// 	this.$refs.imageView.setLoadingState(true)
			// }, 1000)
		},


		syncQwoachFileData() {
			this.qwoachFileData = this.qwoachFile
			if (this.qwoachFileData) {
				this.fileUploadStatus = FileUploadStatus.COMPLETED
			}
		},

		async handleRemove(qwoachFile) {
			let res = await deleteConfirmDialog({itemName: "image"})
			if (res) {
				await this.deleteFile(qwoachFile)
				this.qwoachFileData = null
				this.fileUploadStatus = null
				this.uploadProgress = 0
				this.$emit("deleted", qwoachFile)
			}
		},

		async deleteFile(qwoachFile) {
			await this.handleAsync(() => this.fileManager.deleteForever(this.ownerProfile.id, [qwoachFile]))
		},

		async handleUpdate(qwoachFile) {
			this.isUpdatingFile = true
			this.prevFile = qwoachFile
			this.pickFile()
		},

		async handlePreview(qwoachFile){
			await imageDialog({
				src: qwoachFile.url
			})
		},


		pickFile() {
			this.$refs.localInput.pickFile()
		},

		async onFileSelected(qwoachFile, fileContent) {
			console.log("onFileSelected: ", qwoachFile)

			this.fileUploadStatus = null
			this.uploadProgress = null

			if(this.parentFolderId){
				qwoachFile.parentId = this.parentFolderId
			}
			this.qwoachFileData = qwoachFile
			this.localFile = {url: fileContent, ...qwoachFile._file}

			await this.fileManager.uploadFiles([qwoachFile], async (qwoachFile) => {
				this.fileUploadStatus = qwoachFile._uploadInfo.status
				this.uploadProgress = qwoachFile._uploadInfo.progress
				console.log("this.uploadProgress = ", this.uploadProgress, this.fileUploadStatus, ", this.isStatusUploaded = ", this.isStatusUploaded)
				if (this.isStatusUploaded) {
					let qwFile = classToClass(this.qwoachFileData, {excludePrefixes: ["_"]})
					if (this.isUpdatingFile) {
						await this.deleteFile(this.prevFile)
						this.isUpdatingFile = false
						this.$emit("updated", qwFile)
					} else {
						this.$emit("uploaded", qwFile)
					}
				}
			})


		},
	},

	computed: {
		computedCssVars() {
			return {
				"--uploader-size": `${this.sizePx}px`
			}
		},

		isCircleComputed(){
			return this.shape === "circle"
		},

		isStatusUploading() {
			return this.fileUploadStatus === FileUploadStatus.IN_PROGRESS
		},
		isStatusUploaded() {
			return this.fileUploadStatus === FileUploadStatus.COMPLETED
		},
		isStatusUploadError() {
			return this.fileUploadStatus === FileUploadStatus.ERROR
		},

		imageFilter() {
			return FileTypesEnum.IMAGES
		}
	},
}
</script>

<style lang="scss">
.qw-uploader-root {
	//&.avatar-uploader .el-upload {
	//	border: 1px dashed #d9d9d9;
	//	border-radius: 6px;
	//	cursor: pointer;
	//	position: relative;
	//	overflow: hidden;
	//}
	//
	//&.avatar-uploader .el-upload:hover {
	//	border-color: #409EFF;
	//}
	//
	//.avatar-uploader-icon {
	//	font-size: 28px;
	//	color: #8c939d;
	//	width: 178px;
	//	height: 178px;
	//	line-height: 178px;
	//	text-align: center;
	//}
	//
	//.avatar {
	//	width: 178px;
	//	height: 178px;
	//	display: block;
	//}
	//
	.el-upload--picture-card, .el-upload-list__item {
		width: var(--uploader-size);
		height: var(--uploader-size);
	}

	.el-upload--picture-card{
		line-height: var(--uploader-size);
	}

	//
	//.el-upload-list__item-status-label {
	//	display: none !important;
	//}

	.preview-container {
		display: flex;
		flex: 1;
		align-items: center;
		justify-content: center;
		height: 100%;
	}

	.el-upload-list__item-thumbnail {
		object-fit: contain;
	}
}

</style>
